import { IMaintenancePriority } from '@/store/api/maintenanceApi';
import { sortMaintenanceSeverityNames } from './maintenance';

export type SeverityChoiceId = 'no_affect' | 'discomfort' | 'no_workaround' | 'cant_use';

export interface SeverityChoice {
  id: SeverityChoiceId;
  label: string;
  urgent: boolean;
  /** 0 = most severe, 1 = least severe (position over the sorted priority list) */
  severityRatio: number;
}

// Ordered most-severe -> least-severe (display order, top = most urgent).
export const SEVERITY_CHOICES: SeverityChoice[] = [
  { id: 'cant_use', label: "Can't use the apartment", urgent: true, severityRatio: 0 },
  { id: 'no_workaround', label: 'No reasonable workaround available', urgent: false, severityRatio: 1 / 3 },
  { id: 'discomfort', label: 'It creates discomfort', urgent: false, severityRatio: 2 / 3 },
  { id: 'no_affect', label: "It doesn't affect me at the moment", urgent: false, severityRatio: 1 },
];

const sortedPriorities = (priorities: IMaintenancePriority[]): IMaintenancePriority[] =>
  [...priorities].sort((a, b) => sortMaintenanceSeverityNames(a.name, b.name));

/**
 * Deterministically pick a Yardi priority for a resident urgency choice by its
 * position over the property's sorted priority list (most -> least severe).
 * Replaces the previous brittle keyword matching.
 */
export const resolveSeverityPriority = (
  choiceId: SeverityChoiceId,
  priorities: IMaintenancePriority[],
): IMaintenancePriority | undefined => {
  const choice = SEVERITY_CHOICES.find((c) => c.id === choiceId);
  const sorted = sortedPriorities(priorities);
  if (!choice || sorted.length === 0) return undefined;
  const index = Math.round(choice.severityRatio * (sorted.length - 1));
  return sorted[Math.min(Math.max(index, 0), sorted.length - 1)];
};

/** Reverse: which choice does a stored priority name correspond to (for highlighting). */
export const choiceIdForPriorityName = (
  name: string,
  priorities: IMaintenancePriority[],
): SeverityChoiceId | null => {
  const sorted = sortedPriorities(priorities);
  const idx = sorted.findIndex((p) => p.name === name);
  if (idx === -1) return null;
  const ratio = sorted.length <= 1 ? 0 : idx / (sorted.length - 1);
  let best: SeverityChoice = SEVERITY_CHOICES[0];
  for (const c of SEVERITY_CHOICES) {
    if (Math.abs(c.severityRatio - ratio) < Math.abs(best.severityRatio - ratio)) best = c;
  }
  return best.id;
};
